import { useMemo } from 'react'
import { TriggerConfig, TriggerState } from '../types'

/**
 * Detects if the user is currently typing a trigger (@ for mentions, : for emojis)
 * by scanning backwards from the caret position.
 *
 * Returns the trigger state if found, or null if no trigger is active.
 */
export function useTriggerDetection(
  value: string,
  caretPosition: number,
  triggers: TriggerConfig[],
  isDesktop: boolean
): TriggerState | null {
  return useMemo(() => {
    if (!value || caretPosition < 0) {
      return null
    }

    const textBeforeCaret = value.substring(0, caretPosition)

    // Find all valid triggers and return the one closest to the caret
    let bestTrigger: TriggerState | null = null
    let bestTriggerIndex = -1

    for (const trigger of triggers) {
      // Skip emoji trigger on mobile (native keyboard has emojis)
      if (trigger.type === 'emoji' && !isDesktop) {
        continue
      }

      // Find the last occurrence of the trigger character
      const triggerIndex = textBeforeCaret.lastIndexOf(trigger.char)

      if (triggerIndex === -1) {
        continue
      }

      // Skip if we already found a closer trigger
      if (triggerIndex <= bestTriggerIndex) {
        continue
      }

      // Check if trigger is at word boundary (start of string or preceded by whitespace)
      if (triggerIndex > 0) {
        const charBefore = textBeforeCaret[triggerIndex - 1]
        if (!/\s/.test(charBefore)) {
          continue
        }
      }

      // Extract the query (text between trigger and caret)
      const query = textBeforeCaret.substring(triggerIndex + 1)

      // Validate query based on trigger type
      if (trigger.type === 'user') {
        // For mentions, allow spaces in query (names can have spaces)
        // But don't allow newlines
        if (query.includes('\n')) {
          continue
        }
      } else if (trigger.type === 'emoji') {
        // For emojis, only allow alphanumeric, underscore, plus, minus
        if (!/^[a-zA-Z0-9_+-]*$/.test(query)) {
          continue
        }
      }

      bestTrigger = {
        type: trigger.type,
        query,
        triggerIndex,
      }
      bestTriggerIndex = triggerIndex
    }

    return bestTrigger
  }, [value, caretPosition, triggers, isDesktop])
}
